import * as React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Box, ButtonBase, CssBaseline, GlobalStyles } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MainHeader from "../../components/MainHeader";
import HeaderSecti from "../../components/HeaderSecti";
import Rodape from "../../components/Rodape";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import { Bloco3D } from "../OxetechAcademy/ui";

// Central de ajuda (/ajuda) no padrao das telas de Login/Cadastro.
//
// Os textos descrevem o que a plataforma FAZ hoje:
//  - "Esqueci minha senha" manda uma SENHA PROVISORIA por e-mail (nao existe
//    link de recuperacao com validade — decisao de 08/09/2026 de manter o
//    texto honesto);
//  - "Alterar o e-mail" pede CPF/CNPJ + a senha atual.
//
// WhatsApp, horario e duvidas vem de /config/ajuda.json, lido a cada acesso:
// da para editar sem deploy (ver o comentario dentro do JSON).
const FONTE = "'Poppins', system-ui, sans-serif";
const COR = {
  fundo: "#FFF9EF",
  borda: "#F0E6D6",
  tinta: "#1F1E1C",
  suave: "#6B655D",
  apagado: "#8C8478",
  laranja: "#D2551D",
  azul: "#2C5FA8",
  verde: "#2F7A5B",
};

const PADRAO = {
  whatsapp_url: "https://wa.me/message/G622HMB7DH3KM1",
  whatsapp_horario: "",
  duvidas: [
    {
      pergunta: "Não recebi o e-mail com a senha provisória. O que faço?",
      resposta:
        "Confira a caixa de spam ou promoções e se o e-mail digitado é o mesmo do cadastro. Se você não tem mais acesso a esse e-mail mas lembra a senha, use “Alterar o e-mail cadastrado”. Se não lembra nenhum dos dois, fale com a equipe pelo WhatsApp.",
    },
  ],
};

function CartaoOpcao({ icone, bloco, titulo, texto, acao, to, href, cor }) {
  const props = href ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" } : { component: Link, to };
  return (
    <Box
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        p: "28px",
        borderRadius: "20px",
        bgcolor: "#fff",
        border: `1px solid ${COR.borda}`,
        boxShadow: "0 14px 34px rgba(70,40,10,.08)",
        color: "inherit",
        textDecoration: "none",
        minWidth: 0,
        outline: "none",
        transition: "transform .2s, box-shadow .2s",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 22px 44px rgba(70,40,10,.13)" },
        "&:focus-visible": { boxShadow: `0 0 0 3px #fff, 0 0 0 6px ${cor}` },
      }}
    >
      <Bloco3D cor={bloco} tamanho={56} icone={icone} />
      <Box component="h2" sx={{ m: "8px 0 0", fontSize: 19, fontWeight: 700, lineHeight: 1.35, color: COR.tinta }}>
        {titulo}
      </Box>
      <Box sx={{ fontSize: 14.5, lineHeight: 1.6, color: COR.suave, mb: "14px" }}>{texto}</Box>
      <Box component="span" sx={{ mt: "auto", display: "inline-flex", alignItems: "center", gap: "8px", fontSize: 15, fontWeight: 600, color: cor }}>
        {acao}
        {href ? <OpenInNewRoundedIcon sx={{ fontSize: 18 }} /> : <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
      </Box>
    </Box>
  );
}

function Duvida({ pergunta, resposta, aberta, onToggle, id }) {
  return (
    <Box sx={{ borderTop: `1px solid ${COR.borda}`, "&:first-of-type": { borderTop: "none" } }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={aberta}
        aria-controls={id}
        sx={{
          width: "100%",
          justifyContent: "space-between",
          gap: "16px",
          p: { xs: "18px 20px", md: "22px 32px" },
          textAlign: "left",
          fontFamily: FONTE,
          fontSize: 16,
          fontWeight: 600,
          color: COR.tinta,
          "&.Mui-focusVisible": { bgcolor: "#FDF8F0" },
        }}
      >
        {pergunta}
        <KeyboardArrowDownRoundedIcon sx={{ flex: "none", color: COR.apagado, transition: "transform .2s", transform: aberta ? "rotate(180deg)" : "none" }} />
      </ButtonBase>
      {aberta && (
        <Box id={id} className="ajuda-rise" sx={{ px: { xs: "20px", md: "32px" }, pb: "22px", fontSize: 15, lineHeight: 1.65, color: COR.suave }}>
          {resposta}
        </Box>
      )}
    </Box>
  );
}

export default function Help() {
  const signed = useSelector((s) => s?.auth?.signed);
  const [aberta, setAberta] = React.useState(0);
  const [config, setConfig] = React.useState(PADRAO);

  React.useEffect(() => {
    let vivo = true;
    fetch(`/config/ajuda.json?t=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        if (!vivo || !c) return;
        setConfig({
          whatsapp_url: c?.whatsapp_url || PADRAO.whatsapp_url,
          whatsapp_horario: c?.whatsapp_horario || "",
          duvidas: Array.isArray(c?.duvidas) && c.duvidas.length ? c.duvidas : PADRAO.duvidas,
        });
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  const opcoes = [
    {
      bloco: "laranja",
      icone: <LockOutlinedIcon />,
      titulo: "Esqueci minha senha",
      texto: "Receba uma senha provisória no e-mail do cadastro e troque por uma nova depois de entrar.",
      acao: "Recuperar senha",
      to: "/recuperar-senha",
      cor: COR.laranja,
    },
    {
      bloco: "azul",
      icone: <MailOutlineRoundedIcon />,
      titulo: "Alterar o e-mail cadastrado",
      texto: "Perdeu o acesso ao e-mail antigo? Confirme seu CPF e sua senha atual e informe o novo.",
      acao: "Solicitar alteração",
      to: "/alterar-email",
      cor: COR.azul,
    },
    {
      bloco: "verde",
      icone: <ChatBubbleOutlineRoundedIcon />,
      titulo: "Falar com a equipe",
      texto: config.whatsapp_horario
        ? `Atendimento pelo WhatsApp, ${config.whatsapp_horario}.`
        : "Não resolveu por aqui? Fale com a equipe do OxeTech pelo WhatsApp.",
      acao: "Abrir WhatsApp",
      href: config.whatsapp_url,
      cor: COR.verde,
    },
  ];

  return (
    <React.Fragment>
      <CssBaseline />
      <GlobalStyles
        styles={{
          "@keyframes ajudaRiseIn": { from: { opacity: 0, transform: "translateY(14px)" }, to: { opacity: 1, transform: "none" } },
          ".ajuda-rise": { animation: "ajudaRiseIn .6s cubic-bezier(.2,.7,.3,1) both" },
          "@media (prefers-reduced-motion: reduce)": { ".ajuda-rise": { animation: "none" } },
        }}
      />
      {/* Logado, o layout padrao ja desenha o header da plataforma. */}
      {!signed && (
        <>
          <HeaderSecti />
          <MainHeader />
        </>
      )}
      <Box
        component="main"
        sx={{ position: "relative", overflow: "hidden", bgcolor: COR.fundo, fontFamily: FONTE, px: "16px", py: { xs: "40px", md: "64px" } }}
      >
        <Box aria-hidden sx={{ position: "absolute", left: -180, top: 60, width: 420, height: 420, borderRadius: "50%", bgcolor: "#FCEFD6" }} />
        <Box aria-hidden sx={{ position: "absolute", right: -160, bottom: -160, width: 420, height: 420, borderRadius: "50%", bgcolor: "#FBE7D3" }} />

        <Box sx={{ position: "relative", maxWidth: 980, mx: "auto", display: "flex", flexDirection: "column", gap: { xs: "32px", md: "40px" } }}>
          <Box className="ajuda-rise" sx={{ maxWidth: 620, mx: "auto", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <Box sx={{ fontSize: 13, fontWeight: 600, letterSpacing: ".22em", textTransform: "uppercase", color: COR.azul }}>Central de ajuda</Box>
            <Box component="h1" sx={{ m: 0, fontSize: { xs: 34, md: 42 }, lineHeight: 1.12, fontWeight: 700, letterSpacing: "-.02em", color: COR.laranja }}>
              Como podemos te ajudar?
            </Box>
            <Box sx={{ fontSize: { xs: 15.5, md: 17 }, lineHeight: 1.6, color: COR.suave }}>
              Escolha o que está acontecendo. Resolvemos a maioria dos casos em poucos minutos, sem precisar falar com ninguém.
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))", gap: "22px" }}>
            {opcoes.map((o) => (
              <CartaoOpcao key={o.titulo} {...o} />
            ))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <Box component="h2" sx={{ m: 0, fontSize: 13, fontWeight: 600, letterSpacing: ".18em", textTransform: "uppercase", color: COR.apagado }}>
              Dúvidas frequentes
            </Box>
            <Box sx={{ borderRadius: "20px", bgcolor: "#fff", border: `1px solid ${COR.borda}`, overflow: "hidden" }}>
              {config.duvidas.map((d, i) => (
                <Duvida
                  key={d?.pergunta || i}
                  id={`ajuda-duvida-${i}`}
                  pergunta={d?.pergunta}
                  resposta={d?.resposta}
                  aberta={aberta === i}
                  onToggle={() => setAberta(aberta === i ? -1 : i)}
                />
              ))}
            </Box>
          </Box>

          <Box
            component={Link}
            to={signed ? "/dashboard" : "/login"}
            sx={{ alignSelf: "center", display: "inline-flex", alignItems: "center", gap: "8px", fontSize: 16, color: COR.apagado, textDecoration: "none", "&:hover": { color: COR.laranja } }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            {signed ? "Voltar ao meu painel" : "Voltar para o login"}
          </Box>
        </Box>
      </Box>
      <Rodape />
    </React.Fragment>
  );
}
